// The viewer catalogue as the portal presents it: GET /users/viewers
// serves the viewers of the user's organisation with the fields that say how
// each is shown (slug, population, counterpart, show_population_tabs,
// show_page_navigation, back-ratings #13); these functions put them in order,
// group them for the menus, give them routes and find a viewer's pair.
// Split out of Visor.jsx on 2026-09-21 with no change in behaviour.

// Menu order of tipo_visor (the back's static/constants.py): Modelo 2
// (8 Personas, 7 Hogares), Modelo 1 (6, 5), Modelo 0 (4, 3), Universo Claro
// (1, 0), Conjunto (2), Otros (9). Personas before Hogares since 2026-10-13
// (the owner's decision), so the viewer that opens by default, the first of
// the catalogue, is the Personas one of the newest month. Modelo 1 and Modelo 0 are what the portal
// called Nuevo Universo and Modelo 1 before Modelo 2, which reaches clients on
// 2026-10-01 (back-ratings #61); the integers did not change.
const TYPE_ORDER = [8, 7, 6, 5, 4, 3, 1, 0, 2, 9];

// A type the portal does not know yet goes after all of them. It used to
// sort first, so the first viewer of a new type opened by default in no
// menu, and the back had to hold its viewers until the portal knew it.
const typeRank = (viewer) => {
  const rank = TYPE_ORDER.indexOf(viewer.tipo_visor);
  return rank === -1 ? TYPE_ORDER.length : rank;
};

// The model a viewer belongs to: 0, 1, 2, or null for none (Universo Claro,
// Conjunto, most of Otros). The back serves it as `model` since 2026-10-01
// (back-ratings' answer to our handoff of that day), derived from the type
// for the monthly viewers and set in the admin for the special ones, such as
// Datos Canales. Without the field, the monthly types say it.
const MODEL_OF_TYPE = { 3: 0, 4: 0, 5: 1, 6: 1, 7: 2, 8: 2 };
export const modelOf = (viewer) => {
  if (viewer.model !== undefined) return viewer.model;
  const model = MODEL_OF_TYPE[viewer.tipo_visor];
  return model === undefined ? null : model;
};

// The newest model first, then the viewers of no model.
const MODEL_ORDER = [2, 1, 0, null];
const modelRank = (viewer) => {
  const rank = MODEL_ORDER.indexOf(modelOf(viewer));
  return rank === -1 ? MODEL_ORDER.length : rank;
};

// A viewer that is not one month of ratings: in a model's group it comes
// after the months, and its button has an icon of its own.
const MONTHLY_TYPES = [0, 1, 3, 4, 5, 6, 7, 8];
export const isSpecial = (viewer) => !MONTHLY_TYPES.includes(viewer.tipo_visor);

// The icon of a viewer's button, the same in every group of the menu: the
// calendar for a month (types 0 to 8, the 5-minute ones included), a chart
// for a special viewer of a model (the Datos Canales pairs, the only ones on
// 2026-10-13) and a document for any other (Chile, OTTs and the like).
export const viewerIcon = (viewer) => {
  if (isSpecial(viewer) && modelOf(viewer) !== null) return 'stats-chart-outline';
  if (viewer.tipo_visor >= 0 && viewer.tipo_visor <= 8) return 'calendar-clear-outline';
  return 'document-text-outline';
};

// By model, then by type in that order, then newest year and month first,
// then Personas before the rest (the pairs of one type, such as Datos
// Canales), then by label. For the monthly viewers the model follows the type, so their
// order is the one Visor.jsx always had; a special viewer of a model sorts
// after that model's months. The first viewer opens by default, and it is
// the first of the group at the top of the menu.
export const sortViewers = (viewers) => [...viewers].sort((a, b) => {
  const modelA = modelRank(a);
  const modelB = modelRank(b);
  if (modelA < modelB) return -1;
  if (modelA > modelB) return 1;
  const typeA = typeRank(a);
  const typeB = typeRank(b);
  if (typeA < typeB) return -1;
  if (typeA > typeB) return 1;
  if (a.anio > b.anio) return -1;
  if (a.anio < b.anio) return 1;
  if (a.mes > b.mes) return -1;
  if (a.mes < b.mes) return 1;
  const peopleA = a.population === 'people' ? 0 : 1;
  const peopleB = b.population === 'people' ? 0 : 1;
  if (peopleA !== peopleB) return peopleA - peopleB;
  if (a.label < b.label) return -1;
  if (a.label > b.label) return 1;
  return 0;
});

// What each menu lists, from a sorted catalogue. A model's group holds its
// months and, after them, its special viewers. At the top of the menu is the
// newest model the organisation has: Modelo 2, or Modelo 1 for one without
// Modelo 2. Modelo 1 has a group of its own only when it is not at the top.
// An organisation with neither has nothing at the top, as before Modelo 2.
// Otros holds the type-9 viewers of no model. The five-minute menu holds
// every 5-minute viewer except those of Otros, which have their own.
export const groupViewers = (sorted) => {
  const ofTypes = (...types) => sorted.filter((viewer) => types.includes(viewer.tipo_visor));
  const ofModel = (model) => sorted.filter((viewer) => modelOf(viewer) === model);
  const modelo2 = ofModel(2);
  const modelo1 = ofModel(1);
  return {
    latest: modelo2.length > 0 ? modelo2 : modelo1,
    modelo1: modelo2.length > 0 ? modelo1 : [],
    modelo0: ofModel(0),
    universoClaro: ofTypes(0, 1),
    cincoMinutos: sorted.filter((viewer) => viewer.tipo_franja === 1 && viewer.tipo_visor !== 9),
    others: sorted.filter((viewer) => viewer.tipo_visor === 9 && modelOf(viewer) === null),
  };
};

export const viewerRoute = (viewer) => `/visor-rpd/${viewer.slug}`;

// The routes Main knows about: the login, the viewer shell, then one per
// viewer in catalogue order, so a viewer's position in the catalogue is its
// route's position minus two.
export const viewerRoutes = (sorted) => ["/inicio-sesion", "/visor-rpd", ...sorted.map(viewerRoute)];

// The HOGARES/PERSONAS tabs of a viewer: shown when the back asks for them and
// the viewer's counterpart is in the user's catalogue; the tab of each
// population points at its own viewer.
export const populationTabs = (selected, catalogue) => {
  const counterpart = selected
    ? catalogue.find((viewer) => viewer.id === selected.counterpart)
    : undefined;
  return {
    show: Boolean(selected && selected.show_population_tabs && counterpart),
    households: selected && selected.population === 'households' ? selected : counterpart,
    people: selected && selected.population === 'people' ? selected : counterpart,
  };
};

// Where a button of the menu leads. A button stands for a pair and is drawn
// for its Hogares viewer, but opens the Personas one since 2026-10-13 (the
// owner's decision, for every pair): when the organisation has it and the
// tabs that lead back to Hogares are shown; otherwise the viewer itself.
export const buttonTarget = (viewer, catalogue) => {
  if (viewer.population !== 'households') return viewer;
  const tabs = populationTabs(viewer, catalogue);
  return tabs.show ? tabs.people : viewer;
};

// Where the menu draws a viewer's button: the group that holds it, and
// whether it sits behind that model's "Datos Canales Modelo N" button (two or
// more specials). A Personas viewer's button is its counterpart's, so pass
// the button's viewer. The menu opens this path when the page loads on a
// viewer inside a group, which used to start with every group closed (the
// owner's request of 2026-10-13). Null when no group holds it.
const MENU_GROUPS = ['latest', 'modelo1', 'modelo0', 'universoClaro', 'cincoMinutos', 'others'];
const MODEL_GROUPS = ['latest', 'modelo1', 'modelo0'];

export const menuPath = (viewer, groups) => {
  const group = MENU_GROUPS.find((key) => (groups[key] || []).some((v) => v.id === viewer.id));
  if (!group) return null;
  const specials = groups[group].filter((v) => v.population !== 'people' && isSpecial(v));
  return {
    group,
    specials: MODEL_GROUPS.includes(group) && isSpecial(viewer) && specials.length > 1,
  };
};
